import type { InjectedPolicyFundingSource, InjectedSelfFundingChange } from './types';

/**
 * Insert injected policies into primary's `multipleFundingSources.ltcPolicy`.
 * Mutates `client` in place — caller is expected to hand in a clone.
 *
 * Each injection gets a stable `fundingSourceId`. If the caller didn't
 * supply one, a deterministic per-call ID is assigned so snapshots stay
 * stable.
 */
export function applyInjectedPolicies(
  client: Client,
  injectedPolicies: InjectedPolicyFundingSource[],
): void {
  if (injectedPolicies.length === 0) return;

  if (!client.multipleFundingSources) {
    client.multipleFundingSources = { selfFunding: {}, ltcPolicy: {}, annuity: {} };
  }

  if (!client.multipleFundingSources.ltcPolicy) {
    client.multipleFundingSources.ltcPolicy = {};
  }

  let counter = 0;

  for (const injected of injectedPolicies) {
    const id = injected.fundingSourceId ?? `__injected_policy_${++counter}`;
    client.multipleFundingSources.ltcPolicy[id] = injected.policyFundingSource;
  }
}

/**
 * Apply per-source self-funding modifications (lump-sum deductions and
 * recurring drawdowns) to primary's self-funding sources. Mutates `client`
 * in place — caller is expected to hand in a clone.
 *
 * Lump-sum deductions subtract from `existingAssetsValue` at time-0;
 * recurring drawdowns append to the source's `additionalDrawdowns` so the
 * engine debits each year's premium share during the simulation loop.
 *
 * Sources referenced by ID but missing from the client are silently
 * skipped, so callers can pass a single change list that targets either
 * spouse without filtering it first.
 */
export function applyInjectedSelfFundingChanges(
  client: Client,
  changes: InjectedSelfFundingChange[],
): void {
  if (changes.length === 0) return;

  for (const change of changes) {
    const source = client.multipleFundingSources?.selfFunding?.[change.selfFundingSourceId];

    if (!source) continue;

    if (change.lumpSumDeduction != null && source.existingAssetsValue != null) {
      source.existingAssetsValue = Math.max(
        0,
        source.existingAssetsValue - change.lumpSumDeduction,
      );
    }

    if (change.additionalDrawdowns?.length) {
      source.additionalDrawdowns = [
        ...(source.additionalDrawdowns ?? []),
        ...change.additionalDrawdowns,
      ];
    }
  }
}
